import type { ResultSet } from '@cubejs-client/core'
import {
  asRenderableColumnKeys,
  CubeQueryBuilder,
  useCubeQuery,
} from '@driftt/dds.reports'
import { isEmpty, isNil } from 'ramda'
import { useMemo } from 'react'

import {
  DRIFT_CHAT_BLENDED_PLAYBOOK_METRICS_DIMENSIONS,
  PlaybookChatExperimentQuery,
} from '../cube'

export const EMPTY_SET = [0, 0, 0, 0, 0]

export const buildExperimentQuery = (
  playbookId: number,
  playbookVersion: number
) =>
  new CubeQueryBuilder({
    query: PlaybookChatExperimentQuery,
  })
    .withFilters([
      {
        member: DRIFT_CHAT_BLENDED_PLAYBOOK_METRICS_DIMENSIONS.PLAYBOOK_ID,
        operator: 'equals',
        values: [`${playbookId}`],
      },
      {
        member: DRIFT_CHAT_BLENDED_PLAYBOOK_METRICS_DIMENSIONS.PLAYBOOK_VERSION,
        operator: 'equals',
        values: [`${playbookVersion}`],
      },
    ])
    .withOrder([
      [DRIFT_CHAT_BLENDED_PLAYBOOK_METRICS_DIMENSIONS.BOT_FLOW, 'asc'],
    ])
    .build()

const parseChartData = (data: ResultSet) => {
  const tableData = asRenderableColumnKeys(data.tablePivot())

  const mappedData = tableData.map(({ DriftChatBlendedPlaybookMetrics }) => {
    const {
      countConversations,
      countEmailsCaptured,
      countImpressions,
      countMeetingsBooked,
      countSends,
    } = DriftChatBlendedPlaybookMetrics || {}
    return [
      Number(countImpressions ?? 0),
      Number(countSends ?? 0),
      Number(countConversations ?? 0),
      Number(countEmailsCaptured ?? 0),
      Number(countMeetingsBooked ?? 0),
    ]
  })

  if (mappedData.length === 2) {
    return mappedData
  }

  if (tableData.length === 1) {
    return tableData?.[0]?.DriftChatBlendedPlaybookMetrics?.botFlow === 'Flow A'
      ? [...mappedData, EMPTY_SET]
      : [EMPTY_SET, ...mappedData]
  }

  return []
}

export const useExperimentMetrics = (playbookId: number, version: number) => {
  const {
    data: chartData,
    isLoading,
    isError,
  } = useCubeQuery({
    query: buildExperimentQuery(playbookId, version),
  })

  const tableData = useMemo(() => {
    if (isNil(chartData) || isEmpty(chartData)) {
      return []
    }
    return parseChartData(chartData as ResultSet)
  }, [chartData])

  return { data: tableData, isLoading, isError }
}
